import React, { useCallback, useMemo, useState } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { useStyles } from './styles';
import OrderedCheckbox from './OrderedCheckbox';
import { uniq } from 'lodash';

/**
 * @param {import('./index').Props & import('@mui/x-data-grid').DataGridProps} props
 */
export default function useVariantConversion(props) {
    const {
        columns: rawColumns,
        rows,
        variant,
        getRowId,
        getRowUrl,
        selectionType,
        selectionModel: propsSelectionModel,
        onSelectionModelChange: propsSelectionModelChange,
        overrideSelectAll,
        overrideSelectNone,
        ...other
    } = props;

    // Use provided selection model, or fall back to an internal one.
    const [_internalSelectionModel, _setInternalSelectionModel] = useState([]);

    /*
     * When the rows change, clone the model instance, to fix an instability
     *  that happens when using server-side pagination and selection.
     * Without this fix, cells may appear to be deselected
     *  when they are actually selected, after turning the page.
     */
    const selectionModel = useMemo(() => {
        const model =  propsSelectionModel ?? _internalSelectionModel;

        return [...model];
    }, [rows, propsSelectionModel, _internalSelectionModel]);

    const onSelectionModelChange = useCallback(ids => {
        // Remove duplicates - it's possible occasionally when using custom handling.
        ids = uniq(ids);

        if (propsSelectionModelChange) {
            propsSelectionModelChange(ids);
        }
        else {
            _setInternalSelectionModel(ids);
        }
    }, [propsSelectionModelChange]);

    const selectAll = useCallback(() => {
        if (overrideSelectAll) {
            return overrideSelectAll();
        }

        onSelectionModelChange(rows.map(r => getRowId(r)));
    }, [overrideSelectAll, rows, getRowId]);

    const selectNone = useCallback(() => {
        if (overrideSelectNone) {
            return overrideSelectNone();
        }

        onSelectionModelChange([]);
    }, [overrideSelectNone]);

    const classes = useStyles(props);

    // Transform columns
    /** @type {Inputs['columns']} */
    const columns = useMemo(() => {
        let columns = rawColumns;

        if (variant === 'links') {

            // For links variant, prefix first column with edit icon
            {
                let [first, ...rest] = columns;

                const render = first.renderCell;

                first = {
                    ...first,
                    renderCell: (params) => (
                        <>
                            <IconButton size="large">
                                <EditIcon fontSize='small' />
                            </IconButton>

                            {render ? render(params) : params.formattedValue}
                        </>
                    )
                }

                columns = [first, ...rest];
            }

            // And turn cells into links if possible
            if (getRowUrl) {
                columns = columns.map(col => ({
                    ...col,
                    renderCell: params => (
                        <Link to={getRowUrl(params)} className={classes.link}>
                            {col.renderCell?.(params) ?? params.formattedValue}
                        </Link>
                    )
                }));
            }
        }

        if (selectionType === 'ordered-checkboxes' && columns[0].field !== 'ADMIN_PANEL_ORDER_CHECKBOX') {
            columns.unshift({
                headerName: 'Order',
                field: 'ADMIN_PANEL_ORDER_CHECKBOX',
                minWidth: 112,
                renderCell({ id }) {
                    return <OrderedCheckbox rowId={id} />;
                },
                sortable: false,
                filterable: false,
            })
        }

        return columns;
    }, [rawColumns, variant, getRowId, getRowUrl, classes, selectionType]);

    return {
        ...other,

        // Props that were accessed, but must still be passed
        rows,
        getRowId,
        variant,
        selectionType,

        // Props that were transformed
        columns,
        selectionModel, onSelectionModelChange,

        // New props
        classes,
        selectAll,
        selectNone,
    };
}